import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { zIndex } from '@/assets/styles';
import { colors } from '@/assets/styles/colors';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { IconScout } from '@/components/ui/images/IconScout';
import { useCalendarPanel } from '@/hooks/ui/filters/calendar';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TimeComponents {
  hours: number;
  minutes: number;
  seconds: number;
}

type ClockStep = 'hours' | 'minutes' | 'seconds';
type Period = 'AM' | 'PM';

// ─── Style tokens ─────────────────────────────────────────────────────────────

const ACCENT = colors.important.main;
const PERIOD_ACTIVE_COLOR = colors.important.main;
const WHITE = colors.grays.neutralFF;
const DIAL_BG = colors.grays.neutralEE;
const DIAL_NUMBER_COLOR = colors.grays.neutral99;
const CHEVRON_DISABLED_COLOR = colors.grays.neutralCC;
const PANEL_SHADOW = '0 2px 8px rgba(0,0,0,0.08)';
const PANEL_RADIUS = '12px';
const PANEL_WIDTH = '260px';
const PANEL_PADDING = '16px';

// ─── Clock geometry constants ─────────────────────────────────────────────────

const CLOCK_CENTER = 110;
const CLOCK_BG_RADIUS = 105;
const CLOCK_OUTER_RADIUS = 87;
const CLOCK_INNER_RADIUS = 60;
const CLOCK_HAND_OUTER_RADIUS = 77;
const CLOCK_HAND_INNER_RADIUS = 52;
const BUBBLE_OUTER_RADIUS = 14;
const BUBBLE_INNER_RADIUS = 12;
const DEGREES_PER_HOUR = 30;
const DEGREES_PER_MINUTE_SECOND = 6;
const MINUTES_SNAP_STEP = 5;
const OUTER_RING_DIST_RATIO = 0.6;

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function parseTimeString(timeString?: string): TimeComponents {
  if (!timeString || timeString === 'HH:mm:ss' || timeString === 'HH:mm') {
    return { hours: 0, minutes: 0, seconds: 0 };
  }
  const parts = timeString.split(':');
  return {
    hours: parseInt(parts[0] ?? '0', 10) || 0,
    minutes: parseInt(parts[1] ?? '0', 10) || 0,
    seconds: parseInt(parts[2] ?? '0', 10) || 0,
  };
}

function formatTimeString(components: TimeComponents, includeSeconds: boolean): string {
  const paddedHours = components.hours.toString().padStart(2, '0');
  const paddedMinutes = components.minutes.toString().padStart(2, '0');
  const paddedSeconds = components.seconds.toString().padStart(2, '0');
  return includeSeconds
    ? `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
    : `${paddedHours}:${paddedMinutes}`;
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function polarToCartesian(angleDeg: number, radius: number): { x: number; y: number } {
  const radians = toRadians(angleDeg - 90);
  return {
    x: CLOCK_CENTER + radius * Math.cos(radians),
    y: CLOCK_CENTER + radius * Math.sin(radians),
  };
}

function derivePeriod(hours: number): Period {
  return hours < 12 ? 'AM' : 'PM';
}

function hoursTo12(hours: number): number {
  const display = hours % 12;
  return display === 0 ? 12 : display;
}

function periodTo24(hours12: number, period: Period): number {
  if (period === 'AM') return hours12 === 12 ? 0 : hours12;
  return hours12 === 12 ? 12 : hours12 + 12;
}

// ─── StepNavigator ───────────────────────────────────────────────────────────

interface StepNavigatorProps {
  step: ClockStep;
  showSeconds: boolean;
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
}

function StepNavigator({
  step,
  showSeconds,
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
}: StepNavigatorProps) {
  const lastStep: ClockStep = showSeconds ? 'seconds' : 'minutes';
  const isPrevDisabled = step === 'hours';
  const isNextDisabled = step === lastStep;

  const buttonStyle = (disabled: boolean): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    padding: '0 4px',
    marginTop: '4px',
    marginBottom: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div
      style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
      data-testid="step-navigator"
    >
      <button
        type="button"
        disabled={isPrevDisabled}
        onClick={onPrev}
        aria-label={prevLabel}
        data-testid="step-nav-prev"
        style={buttonStyle(isPrevDisabled)}
      >
        <IconScout name="angleLeft" size={22} color={isPrevDisabled ? CHEVRON_DISABLED_COLOR : ACCENT} />
      </button>
      <button
        type="button"
        disabled={isNextDisabled}
        onClick={onNext}
        aria-label={nextLabel}
        data-testid="step-nav-next"
        style={buttonStyle(isNextDisabled)}
      >
        <IconScout name="angleRight" size={22} color={isNextDisabled ? CHEVRON_DISABLED_COLOR : ACCENT} />
      </button>
    </div>
  );
}

// ─── AnalogClockFace ──────────────────────────────────────────────────────────

interface AnalogClockFaceProps {
  step: ClockStep;
  timeComponents: TimeComponents;
  use12Hours: boolean;
  period: Period;
  clockLabel: string;
  onSelectValue: (value: number) => void;
}

function AnalogClockFace({
  step,
  timeComponents,
  use12Hours,
  period,
  clockLabel,
  onSelectValue,
}: AnalogClockFaceProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  function handleSvgClick(event: React.MouseEvent<SVGSVGElement>) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const deltaX = event.clientX - rect.left - rect.width / 2;
    const deltaY = event.clientY - rect.top - rect.height / 2;
    const angle = ((Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90 + 360) % 360;

    if (step === 'hours') {
      const nearestHour12 = Math.round(angle / DEGREES_PER_HOUR) % 12;
      if (use12Hours) {
        const hour12 = nearestHour12 === 0 ? 12 : nearestHour12;
        onSelectValue(periodTo24(hour12, period));
      } else {
        const safeRadius = rect.width / 2 || 1;
        const distRatio = Math.sqrt(deltaX * deltaX + deltaY * deltaY) / safeRadius;
        if (distRatio > OUTER_RING_DIST_RATIO) {
          onSelectValue(nearestHour12 === 0 ? 12 : nearestHour12);
        } else {
          onSelectValue(nearestHour12 === 0 ? 0 : nearestHour12 + 12);
        }
      }
    } else if (step === 'minutes') {
      const degreesPerSnap = DEGREES_PER_MINUTE_SECOND * MINUTES_SNAP_STEP;
      const snappedMinutes = Math.round(angle / degreesPerSnap) * MINUTES_SNAP_STEP;
      onSelectValue(snappedMinutes % 60);
    } else {
      onSelectValue(Math.round(angle / DEGREES_PER_MINUTE_SECOND) % 60);
    }
  }

  function handleMarkerClick(event: React.MouseEvent, value: number) {
    event.stopPropagation();
    onSelectValue(value);
  }

  function renderHoursMarkers(): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];

    if (use12Hours) {
      for (let i = 1; i <= 12; i++) {
        const position = polarToCartesian(i * DEGREES_PER_HOUR, CLOCK_OUTER_RADIUS);
        const value24 = periodTo24(i, period);
        const isSelected = timeComponents.hours === value24;
        nodes.push(
          <g key={`h12-${i}`} onClick={(e) => handleMarkerClick(e, value24)} style={{ cursor: 'pointer' }}>
            <circle cx={position.x} cy={position.y} r={BUBBLE_OUTER_RADIUS} fill={isSelected ? ACCENT : 'transparent'} />
            <text x={position.x} y={position.y} textAnchor="middle" dominantBaseline="central" fontSize="14" fontFamily="Roboto, sans-serif" fill={isSelected ? WHITE : DIAL_NUMBER_COLOR} style={{ userSelect: 'none' }}>{i}</text>
          </g>,
        );
      }
    } else {
      for (let i = 1; i <= 12; i++) {
        const position = polarToCartesian(i * DEGREES_PER_HOUR, CLOCK_OUTER_RADIUS);
        const isSelected = timeComponents.hours === i;
        nodes.push(
          <g key={`h-out-${i}`} onClick={(e) => handleMarkerClick(e, i)} style={{ cursor: 'pointer' }}>
            <circle cx={position.x} cy={position.y} r={BUBBLE_OUTER_RADIUS} fill={isSelected ? ACCENT : 'transparent'} />
            <text x={position.x} y={position.y} textAnchor="middle" dominantBaseline="central" fontSize="14" fontFamily="Roboto, sans-serif" fill={isSelected ? WHITE : DIAL_NUMBER_COLOR} style={{ userSelect: 'none' }}>{i}</text>
          </g>,
        );
      }
      const innerHours = [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0];
      innerHours.forEach((hour, index) => {
        const position = polarToCartesian((index + 1) * DEGREES_PER_HOUR, CLOCK_INNER_RADIUS);
        const isSelected = timeComponents.hours === hour;
        nodes.push(
          <g key={`h-in-${hour}`} onClick={(e) => handleMarkerClick(e, hour)} style={{ cursor: 'pointer' }}>
            <circle cx={position.x} cy={position.y} r={BUBBLE_INNER_RADIUS} fill={isSelected ? ACCENT : 'transparent'} />
            <text x={position.x} y={position.y} textAnchor="middle" dominantBaseline="central" fontSize="11" fontFamily="Roboto, sans-serif" fill={isSelected ? WHITE : DIAL_NUMBER_COLOR} style={{ userSelect: 'none' }}>{hour === 0 ? '00' : hour}</text>
          </g>,
        );
      });
    }

    return nodes;
  }

  function renderMinuteMarkers(currentValue: number): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    for (let i = 0; i < 60; i += MINUTES_SNAP_STEP) {
      const position = polarToCartesian(i * DEGREES_PER_MINUTE_SECOND, CLOCK_OUTER_RADIUS);
      const isSelected = currentValue === i;
      nodes.push(
        <g key={`min-${i}`} onClick={(e) => handleMarkerClick(e, i)} style={{ cursor: 'pointer' }}>
          <circle cx={position.x} cy={position.y} r={BUBBLE_OUTER_RADIUS} fill={isSelected ? ACCENT : 'transparent'} />
          <text x={position.x} y={position.y} textAnchor="middle" dominantBaseline="central" fontSize="14" fontFamily="Roboto, sans-serif" fill={isSelected ? WHITE : DIAL_NUMBER_COLOR} style={{ userSelect: 'none' }}>{i === 0 ? '00' : i}</text>
        </g>,
      );
    }
    return nodes;
  }

  function renderSecondMarkers(currentValue: number): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    const labeledSeconds = new Set([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]);

    for (let i = 0; i < 60; i++) {
      const position = polarToCartesian(i * DEGREES_PER_MINUTE_SECOND, CLOCK_OUTER_RADIUS);
      const isSelected = currentValue === i;

      if (labeledSeconds.has(i)) {
        nodes.push(
          <g key={`sec-${i}`} onClick={(e) => handleMarkerClick(e, i)} style={{ cursor: 'pointer' }}>
            <circle cx={position.x} cy={position.y} r={BUBBLE_OUTER_RADIUS} fill={isSelected ? ACCENT : 'transparent'} />
            <text x={position.x} y={position.y} textAnchor="middle" dominantBaseline="central" fontSize="14" fontFamily="Roboto, sans-serif" fill={isSelected ? WHITE : DIAL_NUMBER_COLOR} style={{ userSelect: 'none' }}>{i === 0 ? '00' : i}</text>
          </g>,
        );
      } else {
        nodes.push(
          <circle
            key={`sec-dot-${i}`}
            cx={position.x}
            cy={position.y}
            r={isSelected ? 5 : 2}
            fill={isSelected ? ACCENT : CHEVRON_DISABLED_COLOR}
            onClick={(e) => handleMarkerClick(e, i)}
            style={{ cursor: 'pointer' }}
          />,
        );
      }
    }

    return nodes;
  }

  function renderHand(): React.ReactNode {
    let angleDeg = 0;
    let useInnerRing = false;

    if (step === 'hours') {
      if (use12Hours) {
        angleDeg = hoursTo12(timeComponents.hours) * DEGREES_PER_HOUR;
      } else {
        const hours = timeComponents.hours;
        if (hours >= 1 && hours <= 12) {
          angleDeg = hours * DEGREES_PER_HOUR;
        } else {
          useInnerRing = true;
          angleDeg = (hours === 0 ? 12 : hours - 12) * DEGREES_PER_HOUR;
        }
      }
    } else if (step === 'minutes') {
      angleDeg = timeComponents.minutes * DEGREES_PER_MINUTE_SECOND;
    } else {
      angleDeg = timeComponents.seconds * DEGREES_PER_MINUTE_SECOND;
    }

    const handRadius = useInnerRing ? CLOCK_HAND_INNER_RADIUS : CLOCK_HAND_OUTER_RADIUS;
    const end = polarToCartesian(angleDeg, handRadius);

    return (
      <>
        <line
          x1={CLOCK_CENTER}
          y1={CLOCK_CENTER}
          x2={end.x}
          y2={end.y}
          stroke={ACCENT}
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx={end.x} cy={end.y} r={4} fill={ACCENT} />
      </>
    );
  }

  const currentValue =
    step === 'minutes'
      ? timeComponents.minutes
      : step === 'seconds'
        ? timeComponents.seconds
        : timeComponents.hours;

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 220 220"
      width="220"
      height="220"
      onClick={handleSvgClick}
      aria-label={clockLabel}
      data-testid="analog-clock"
      style={{ cursor: 'crosshair', display: 'block' }}
    >
      <circle cx={CLOCK_CENTER} cy={CLOCK_CENTER} r={CLOCK_BG_RADIUS} fill={DIAL_BG} />
      {renderHand()}
      <circle cx={CLOCK_CENTER} cy={CLOCK_CENTER} r={4} fill={ACCENT} />
      {step === 'hours'
        ? renderHoursMarkers()
        : step === 'minutes'
          ? renderMinuteMarkers(currentValue)
          : renderSecondMarkers(currentValue)}
    </svg>
  );
}

// ─── PeriodSelector ───────────────────────────────────────────────────────────

interface PeriodSelectorProps {
  period: Period;
  onPeriodChange: (period: Period) => void;
  amLabel: string;
  pmLabel: string;
}

function PeriodSelector({ period, onPeriodChange, amLabel, pmLabel }: PeriodSelectorProps) {
  return (
    <div
      className="flex items-center justify-between"
      style={{ marginTop: '12px', paddingLeft: '8px', paddingRight: '8px' }}
    >
      {(['AM', 'PM'] as const).map((option) => {
        const isActive = period === option;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onPeriodChange(option)}
            aria-pressed={isActive}
            style={{
              backgroundColor: isActive ? PERIOD_ACTIVE_COLOR : 'transparent',
              color: isActive ? WHITE : DIAL_NUMBER_COLOR,
              fontWeight: 400,
              fontSize: '13px',
              fontFamily: 'Roboto, sans-serif',
              border: 'none',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {option === 'AM' ? amLabel : pmLabel}
          </button>
        );
      })}
    </div>
  );
}

// ─── TimePicker ───────────────────────────────────────────────────────────────

interface TimePickerProps {
  className?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (time: string) => void;
  closeOnOutsideClick?: boolean;
  onOpenChange?: (open: boolean) => void;
  label?: string;
  placeholder?: string;
  variant?: 'floating' | 'standard';
  color?: 'default' | 'important' | 'success' | 'alert' | 'error';
  supportingText?: string;
  tooltipText?: string;
  disabled?: boolean;
  required?: boolean;
  showSeconds?: boolean;
  use12Hours?: boolean;
  /** Color del ícono de reloj del disparador. Por defecto usa el celeste del componente. */
  iconColor?: string;
  'data-testid'?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  className = '',
  value,
  defaultValue = '00:00:00',
  onChange,
  closeOnOutsideClick = true,
  onOpenChange,
  label,
  placeholder,
  variant = 'standard',
  color,
  supportingText,
  tooltipText,
  disabled = false,
  required = false,
  showSeconds = true,
  use12Hours = true,
  iconColor = ACCENT,
  'data-testid': dataTestId,
}) => {
  const { t: translate } = useTranslation();

  const isControlled = typeof value === 'string';
  const [internalTime, setInternalTime] = useState<string>(defaultValue);
  const timeValue = isControlled ? value : internalTime;

  const { open, toggle, close, triggerRef, panelRef } = useCalendarPanel({
    closeOnOutsideClick,
    onOpenChange,
  });

  const effectiveValue = timeValue || defaultValue;

  const [timeComponents, setTimeComponents] = useState<TimeComponents>(() =>
    parseTimeString(effectiveValue),
  );

  const [step, setStep] = useState<ClockStep>('hours');

  const [period, setPeriod] = useState<Period>(() =>
    derivePeriod(parseTimeString(effectiveValue).hours),
  );

  const emittedDefaultRef = useRef(false);

  useEffect(() => {
    if (!emittedDefaultRef.current) {
      if (!isControlled) {
        onChange?.(defaultValue);
      } else if (!value || value === '') {
        onChange?.(defaultValue);
      }
      emittedDefaultRef.current = true;
    }
  }, [isControlled, value, defaultValue, onChange]);

  useEffect(() => {
    const current = isControlled ? value : internalTime;
    const parsed = parseTimeString(current || defaultValue);
    setTimeComponents(parsed);
    if (use12Hours) {
      setPeriod(derivePeriod(parsed.hours));
    }
  }, [isControlled, value, internalTime, defaultValue, use12Hours]);

  function handleToggle() {
    if (!open) setStep('hours');
    toggle();
  }

  function advanceStep() {
    if (step === 'hours') {
      setStep('minutes');
    } else if (step === 'minutes') {
      if (showSeconds) setStep('seconds');
      else close();
    } else {
      close();
    }
  }

  function handleSelectValue(newValue: number) {
    setTimeComponents((previous) => {
      const updated = { ...previous, [step]: newValue };
      const formatted = formatTimeString(updated, showSeconds);
      if (!isControlled) setInternalTime(formatted);
      onChange?.(formatted);
      return updated;
    });
    advanceStep();
  }

  function handlePeriodChange(newPeriod: Period) {
    if (newPeriod === period) return;
    setPeriod(newPeriod);
    setTimeComponents((previous) => {
      const hours24 = periodTo24(hoursTo12(previous.hours), newPeriod);
      const updated = { ...previous, hours: hours24 };
      const formatted = formatTimeString(updated, showSeconds);
      if (!isControlled) setInternalTime(formatted);
      onChange?.(formatted);
      return updated;
    });
  }

  function handlePrevStep() {
    if (step === 'minutes') setStep('hours');
    else if (step === 'seconds') setStep('minutes');
  }

  function handleNextStep() {
    if (step === 'hours') setStep('minutes');
    else if (step === 'minutes' && showSeconds) setStep('seconds');
  }

  const resolvedPlaceholder = placeholder ?? (showSeconds ? 'HH:mm:ss' : 'HH:mm');

  return (
    <div className={`relative ${className}`} ref={triggerRef} data-testid={dataTestId}>
      <div onClick={disabled ? undefined : handleToggle}>
        <LabelInput
          variant={variant}
          color={color}
          label={label}
          value={effectiveValue}
          readOnly
          disabled={disabled}
          required={required}
          onChange={() => {}}
          iconRight={<IconScout name="clockThree" size="md" color={iconColor} />}
          fullWidth
          placeholder={resolvedPlaceholder}
          supportingText={supportingText}
          tooltipText={tooltipText}
        />
      </div>

      {open && (
        <div ref={panelRef} className="absolute left-0 mt-2" style={{ zIndex: zIndex.dropdown }}>
          <div
            style={{
              background: WHITE,
              borderRadius: PANEL_RADIUS,
              padding: PANEL_PADDING,
              boxShadow: PANEL_SHADOW,
              width: PANEL_WIDTH,
            }}
          >
            <StepNavigator
              step={step}
              showSeconds={showSeconds}
              prevLabel={translate('common:timePicker.prevStep')}
              nextLabel={translate('common:timePicker.nextStep')}
              onPrev={handlePrevStep}
              onNext={handleNextStep}
            />

            <AnalogClockFace
              step={step}
              timeComponents={timeComponents}
              use12Hours={use12Hours}
              period={period}
              clockLabel={translate('common:timePicker.analogClock')}
              onSelectValue={handleSelectValue}
            />

            {use12Hours && (
              <PeriodSelector
                period={period}
                onPeriodChange={handlePeriodChange}
                amLabel={translate('common:timePicker.am')}
                pmLabel={translate('common:timePicker.pm')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
